import { useEffect, useMemo, useRef, useState } from "react";

function usePullToRefresh(onRefresh, options = {}) {
  const threshold = Number(options.threshold || 78);
  const maxPull = Number(options.maxPull || 110);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(null);
  const activeRef = useRef(false);

  useEffect(() => {
    if (!refreshing) return undefined;
    let mounted = true;
    Promise.resolve(onRefresh?.())
      .catch(() => {})
      .finally(() => {
        if (!mounted) return;
        setRefreshing(false);
        setPullDistance(0);
      });
    return () => {
      mounted = false;
    };
  }, [onRefresh, refreshing]);

  const bind = useMemo(
    () => ({
      onTouchStart: (event) => {
        if (refreshing) return;
        if ((window.scrollY || document.documentElement.scrollTop || 0) > 0) return;
        startYRef.current = event.touches?.[0]?.clientY ?? null;
        activeRef.current = true;
      },
      onTouchMove: (event) => {
        if (!activeRef.current || startYRef.current == null || refreshing) return;
        const currentY = event.touches?.[0]?.clientY ?? startYRef.current;
        const delta = currentY - startYRef.current;
        if (delta <= 0) {
          setPullDistance(0);
          return;
        }
        if (typeof event.preventDefault === "function") {
          event.preventDefault();
        }
        setPullDistance(Math.min(maxPull, delta * 0.6));
      },
      onTouchEnd: () => {
        activeRef.current = false;
        startYRef.current = null;
        if (pullDistance >= threshold && !refreshing) {
          setRefreshing(true);
          return;
        }
        setPullDistance(0);
      },
      onTouchCancel: () => {
        activeRef.current = false;
        startYRef.current = null;
        if (!refreshing) setPullDistance(0);
      },
    }),
    [maxPull, pullDistance, refreshing, threshold]
  );

  return {
    bind,
    pullDistance,
    refreshing,
    ready: pullDistance >= threshold,
  };
}

export default usePullToRefresh;
